"use client";

import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { useForm } from "react-hook-form"

import { useAuth } from "@/provider";
import { useMessages, useMessageMutation } from "@/lib/swr/messages";
import { translate } from "@/lib/translator";
import { classNames } from "@/lib/classnames";

import YAMessage from "@/components/global/message";
import YAFormResponse from "@/components/global/form-response";
import YAPagination from "@/components/global/pagination";
import YASlideOver from "@/components/global/slide-over";
import YATextInput from "@/components/inputs/text-input";

export const ScrollConversation = ({
  setConversationOpen,
  conversationOpen,
  onSuccess,
}) => {
  const {
    selectedClassroom,
    selectedLocation,
    selectionComplete,
    selectedStudent,
    selectedConversation,
    setSelectedConversation,
    isOnline,
    isSyncing,
  } = useAuth();

  const [modalHasRendered, setModalHasRendered] = useState(false);
  const initialScrollDoneRef = useRef(false);

  const { cache, mutate } = useSWRConfig();

  const commentInputRef = useRef(null);

  const [messagesPage, setMessagesPage] = useState(1);

  const defaultValues = {
		messageText: "",
	}

  const targetDivRef = useRef(null);
  const scrollToDiv = () => {
    if (targetDivRef.current) {
      targetDivRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const {
    register,
		control,
    handleSubmit,
    watch,
    getValues,
		reset,
    formState: { errors, isValid, dirtyFields },
  } = useForm({
    defaultValues
  })

  const canFetchMessages = (
		selectedClassroom?.id &&
    selectedConversation?.id &&
		modalHasRendered
	);

  const {
    data: messages,
    mutate: mutateMessages,
    isLoading: messagesLoading,
    error: messagesError,
    isValidating: messagesValidating,
    success: messagesSuccess,
  } = useMessages({
    waitUntil: canFetchMessages,
    args: {
      page: messagesPage,
      classroomId: selectedClassroom?.id,
      parentId: selectedConversation?.id,
      distinct: false,
    },
    options: {
      keepPreviousData: true,
    },
  });

	useEffect(() => {
    if (modalHasRendered && selectedConversation?.id && messages && !initialScrollDoneRef.current) {
      setTimeout(() => {
        scrollToDiv();
      }, 0);
      initialScrollDoneRef.current = true;
    }
  }, [modalHasRendered, selectedConversation?.id, messages]);

  const formFields = watch()
  const previousConversationId = useRef(null);

  useEffect(() => {
    if (!selectedConversation) {
      setMessagesPage(1);
      initialScrollDoneRef.current = false;
    } else {
      if (selectedConversation?.id > 0 && selectedConversation?.id !== previousConversationId.current) {
        previousConversationId.current = selectedConversation?.id;
        initialScrollDoneRef.current = false;
        setMessagesPage(1);
      } else if (!selectedConversation?.id) {
        initialScrollDoneRef.current = false;
      }
    }
  }, [selectedConversation]);

  useEffect(() => {
    function getData() {
      setTimeout(() => {
        scrollToDiv();
      }, 0);
    }
    if (messages?.results?.length > 0) {
        getData();
    }
  }, [messagesPage, messages]);

  const resultsLoading =
    !selectionComplete ||
    (messagesLoading && !(messages?.results?.length > 0)) ||
    (messagesValidating && !(messages?.results?.length > 0));

  const noResults =
    !isSyncing &&
    !resultsLoading &&
    selectionComplete &&
    ((messagesSuccess && messages?.results?.length === 0) ||
      !selectedClassroom?.id);

  const { 
    trigger: triggerMessage, 
    isMutating: messageIsMutating,
    success: messageSuccess,
    error: messageError,
    reset: resetMessageMutation,
  } =
    useMessageMutation({
      options: {
        onSuccess: async (data, message) => {
          reset(defaultValues);
          for (const key of cache.keys()) {
            if (key.includes("/message")) {
              mutate(
                key,
                (messageResults) => {
                  if (Array.isArray(messageResults)) {
                    return messageResults.map((p) => ({
                      ...p,
                      results: p?.results?.map((result) =>
                        result.id === selectedConversation?.id
                          ? { ...result, ...data }
                          : result
                      ),
                    }));
                  } else {
                    return {
                      ...messageResults,
                      results: messageResults?.results?.map((result) =>
                        result.id === selectedConversation?.id
                          ? { ...result, ...data }
                          : result
                      ),
                    };
                  }
                },
                {
                  rollbackOnError: true,
                  revalidate: true,
                  revalidateAll: true,
                }
              );
            }
          }
          setMessagesPage(1);
          onSuccess && (await onSuccess());
          toast(message, {
            type: "success",
            containerId: "eventToast",
          });
        },
        onError: async (data) => {
          toast(data.toString(), {
            type: "error",
            containerId: "eventToast",
          });
        },
      },
    });

  const canSubmit = isValid &&
    !messagesLoading &&
    !messagesValidating &&
    !messageIsMutating;

  const handleSubmitMessage = (formData) => {
    if(!canSubmit) return;
    document.activeElement.blur();
    triggerMessage({
      data: {
        parentId: selectedConversation?.id,
        classroomId: selectedClassroom?.id,
        message: formFields.messageText,
      }
    });
  };

  return (
    <>
      <YASlideOver
        open={conversationOpen}
        setOpen={setConversationOpen}
        onAfterLeave={() => {
          resetMessageMutation();
          reset(defaultValues);
          setSelectedConversation(null);
          setModalHasRendered(false);
          initialScrollDoneRef.current = false;
        }}
        onBeforeEnter={() => {
          reset(defaultValues);
          setModalHasRendered(true);
          initialScrollDoneRef.current = false;
        }}
        title={
          selectedConversation ? selectedConversation.fullName : "None Selected"
        }
        privacyActionName={selectedConversation ? `Conversation ${selectedConversation.id}` : "None Selected"}
        footer={
          <>
            <form onSubmit={handleSubmit(handleSubmitMessage)}>
              <YATextInput
                placeholder={
                  isOnline
                    ? translate("Write a message...")
                    : translate("Go online to send a message")
                }
                // ref={commentInputRef}
                disabled={!isOnline}
                {...register("messageText", { required: true })}
              />
            </form>
          </>
        }
      >
        <div className="h-20 -mt-20" ref={targetDivRef}></div>
        
        {!isOnline && <div className="sticky top-20 z-10">
            <div className="bg-white">
            <YAFormResponse 
              variant="info" 
              message={"You must be online to receive any new messages."} 
            />
            </div>
          </div>}

        <div className="flex-1 flex flex-col gap-4">
          {!resultsLoading && (
            <div className="pt-4 flex flex-col gap-4 px-4">
              {messages?.results?.map((msg) => {
                let sentBy =
                  msg.sender && msg.sender === "C"
                    ? msg.staffMember
                    : msg.parent
                    ? msg.parent
                    : msg.staffMember;
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                    }}
                    exit={{ opacity: 0 }}
                    className={classNames("relative")}
                  >
                    <div className="">
                      <YAMessage
                        key={msg.id}
                        author={sentBy}
                        text={msg.message}
                        date={msg.createdAt}
                        reverse={
                          msg.sender && msg.sender === "C" ? false : true
                        }
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          

          {messagesError && (
            <div className="px-4 py-4">
              <YAFormResponse variant="danger">{messagesError}</YAFormResponse>
            </div>
          )}

          {noResults && (
            <div className="px-4 py-4">
              <p className="text-xs font-semibold text-ymcagray-600">
                {translate("No results.")}
              </p>
            </div>
          )}

          {resultsLoading && (
            <div className="px-4 py-4">
              <p className="text-xs font-semibold text-ymcagray-600">
                {translate("Loading...")}
              </p>
            </div>
          )}

          <div className="bg-white/50 backdrop-blur py-2 sticky bottom-0 border-t border-black/5 mt-auto px-4">
            <YAPagination
              obj={messages}
              loading={resultsLoading}
              next={() => {
                setMessagesPage(messagesPage + 1);
              }}
              previous={() => {
                setMessagesPage(messagesPage - 1);
              }}
            />
          </div>
        </div>
      </YASlideOver>
    </>
  );
};
